import { createContext, useContext, type ReactNode } from 'react';
import { hasPermission } from '@saas/shared';

/**
 * 認証状態の契約。実装（トークン保持・/auth/me の取得）は apps/web/src/api/auth.tsx。
 * 型は GET /auth/me の形（@saas/module-core/api の UserSummary / TenantSummary と構造的に一致）。
 */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  departmentId: string | null;
  managerId: string | null;
  status: string;
}

export interface AuthTenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  settings: Record<string, unknown>;
}

export interface AuthMe {
  user: AuthUser;
  tenant: AuthTenant;
  roles: string[];
  permissions: string[];
  enabledModules: string[];
}

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface LoginInput {
  /** 開発環境（サブドメインなし）でだけ使う */
  tenantSlug?: string | null;
  email: string;
  password: string;
}

export interface SignupInput {
  tenantName: string;
  slug: string;
  email: string;
  password: string;
  displayName: string;
}

export interface AuthContextValue {
  status: AuthStatus;
  me: AuthMe | null;
  /** RBAC + スコープ（@saas/shared の resolveScope / hasPermission） */
  can(permission: string): boolean;
  canAny(...permissions: string[]): boolean;
  /** owner / admin のどちらかを持つ */
  isAdmin: boolean;
  /** 機能フラグ（me.enabledModules）。無効なモジュールはメニューにもルートにも出さない */
  isModuleEnabled(moduleName: string): boolean;
  login(input: LoginInput): Promise<void>;
  signup(input: SignupInput): Promise<void>;
  logout(): Promise<void>;
  refreshMe(): Promise<void>;
  /** ホスト名にサブドメインが無く、テナント slug の入力欄が要る（開発環境） */
  needsTenantSlug: boolean;
  /** 直近に使ったテナント slug（ログイン欄の初期値） */
  storedTenantSlug: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthContextProvider({ value, children }: { value: AuthContextValue; children: ReactNode }) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('AuthContextProvider の外で useAuth() が呼ばれました');
  return v;
}

/** 認証済みの画面で使う。未認証なら例外（RequireAuth の内側でだけ呼ぶこと） */
export function useMe(): AuthMe {
  const { me } = useAuth();
  if (!me) throw new Error('未認証の状態で useMe() が呼ばれました');
  return me;
}

export function useCan(permission: string): boolean {
  return useAuth().can(permission);
}

/** 純関数版（Provider 実装から使う） */
export function makeCan(permissions: readonly string[] | undefined) {
  return (permission: string): boolean => (permissions ? hasPermission(permissions, permission) : false);
}
